import { Behavior, BehaviorConfig } from './Behavior';
import { Entity } from '../base/Entity';

/**
 * Configuration for a single loot drop
 */
export interface LootDrop {
  /** Type of drop: 'health', 'plasmabomb', 'doublelaser', 'biomoney', etc. */
  type: string;
  /** Probability 0.0-1.0 (1.0 = 100%). For 'all' mode, use 1.0 for guaranteed drops */
  chance: number;
  /** Amount of ammo for ammo drops */
  amount?: number;
  /** Number of items to spawn (default 1) */
  count?: number;
  /** Image point name for spawn position (optional) */
  imagePoint?: string;
}

/**
 * Configuration for LootBehavior
 */
export interface LootConfig extends BehaviorConfig {
  /** List of possible drops */
  drops?: LootDrop[];
  /**
   * Spawn mode:
   * - 'random': Roll once, only ONE item can drop based on cumulative probability
   * - 'all': All drops with chance >= 1.0 are guaranteed, others roll individually
   */
  spawnMode?: 'random' | 'all';
  /**
   * Event that triggers loot spawning (default: 'death')
   * Options: 'death', 'damaged', 'phaseChanged'
   */
  triggerEvent?: string;
}

/**
 * LootBehavior - Generic behavior for handling drops/loot from any entity
 *
 * Supports two spawn modes:
 * - 'random': Loot table system - only ONE item can drop per trigger (used by normal structures)
 * - 'all': All drops are spawned, each with their own count (used by stores)
 *
 * Usage:
 * ```typescript
 * // Normal structure: 50% health OR 50% bomb
 * entity.addBehavior(new LootBehavior(entity, {
 *   spawnMode: 'random',
 *   drops: [
 *     { type: 'health', chance: 0.5 },
 *     { type: 'plasmabomb', chance: 0.5 }
 *   ]
 * }));
 *
 * // Store: 3x health powerups guaranteed
 * entity.addBehavior(new LootBehavior(entity, {
 *   spawnMode: 'all',
 *   drops: [
 *     { type: 'health', chance: 1.0, count: 3, imagePoint: 'Image Point 1' }
 *   ]
 * }));
 * ```
 */
export class LootBehavior extends Behavior {
  private drops: LootDrop[];
  private spawnMode: 'random' | 'all';
  private triggerEvent: string;
  private hasTriggered: boolean = false;

  constructor(entity: Entity, config: LootConfig = {}) {
    super(entity, config);
    this.drops = config.drops ?? [];
    this.spawnMode = config.spawnMode ?? 'random';
    this.triggerEvent = config.triggerEvent ?? 'death';

    // Listen for the trigger event
    this.setupTrigger();
  }

  /**
   * Set up event listener for the trigger
   */
  private setupTrigger(): void {
    switch (this.triggerEvent) {
      case 'death':
        // Listen for death on the entity
        this.entity.on('specialChanged', (state: string) => {
          if (state === 'death' && !this.hasTriggered) {
            this.spawnLoot();
          }
        });
        break;
      case 'damaged':
        this.entity.on('damaged', () => {
          if (!this.hasTriggered) {
            this.spawnLoot();
          }
        });
        break;
      case 'phaseChanged':
        this.entity.on('phaseChanged', () => {
          if (!this.hasTriggered) {
            this.spawnLoot();
          }
        });
        break;
    }
  }

  /**
   * Spawn loot based on configuration
   */
  spawnLoot(): void {
    if (!this.enabled || this.drops.length === 0) return;
    this.hasTriggered = true;

    const entityName = this.entity.texture?.key || 'Unknown';

    if (this.spawnMode === 'random') {
      this.spawnRandomLoot(entityName);
    } else {
      this.spawnAllLoot(entityName);
    }
  }

  /**
   * Random mode: Roll once, only ONE item drops based on cumulative probability
   */
  private spawnRandomLoot(entityName: string): void {
    const roll = Math.random();
    let cumulative = 0;

    for (const drop of this.drops) {
      cumulative += drop.chance;
      if (roll < cumulative) {
        // console.log(`[LootBehavior] ${entityName} → DROP: ${drop.type}${drop.amount ? ` (x${drop.amount})` : ''} | roll=${roll.toFixed(3)} < ${cumulative.toFixed(3)}`);
        this.emitDrop(drop);
        return;
      }
    }

    // console.log(`[LootBehavior] ${entityName} → NO DROP | roll=${roll.toFixed(3)} > totalChance=${cumulative.toFixed(3)}`);
  }

  /**
   * All mode: Spawn all drops with count, each at their image point
   */
  private spawnAllLoot(entityName: string): void {
    for (const drop of this.drops) {
      // For 'all' mode, items with chance < 1.0 still roll
      if (drop.chance < 1.0) {
        const roll = Math.random();
        if (roll >= drop.chance) {
          // console.log(`[LootBehavior] ${entityName} → SKIP: ${drop.type} | roll=${roll.toFixed(3)} >= ${drop.chance.toFixed(3)}`);
          continue;
        }
      }

      const count = drop.count ?? 1;
      // console.log(`[LootBehavior] ${entityName} → DROP: ${drop.type} x${count}${drop.amount ? ` (${drop.amount} each)` : ''}`);

      for (let i = 0; i < count; i++) {
        // For stores with image points, use different image points for each drop
        const adjustedDrop = { ...drop };
        if (drop.imagePoint && count > 1) {
          // Image points are typically named "Image Point 1", "Image Point 2", etc.
          adjustedDrop.imagePoint = `Image Point ${i + 1}`;
        }
        this.emitDrop(adjustedDrop);
      }
    }
  }

  /**
   * Emit spawn event for a single drop
   */
  private emitDrop(drop: LootDrop): void {
    const position = this.getSpawnPosition(drop.imagePoint);

    // NOTE: Falling is handled automatically by PlatformBehavior if spawned in air
    this.scene.events.emit('spawnDrop', {
      type: drop.type,
      x: position.x,
      y: position.y,
      // amount = valor genérico (biomoney), ammo = retrocompatibilidad para drops de munición
      data: drop.amount !== undefined ? { amount: drop.amount, ammo: drop.amount } : undefined,
    });
  }

  /**
   * Get spawn position, optionally using image point
   */
  private getSpawnPosition(imagePointName?: string): { x: number; y: number } {
    if (imagePointName) {
      const point = this.entity.getImagePoint(imagePointName);
      if (point) {
        return point;
      }
      // Image point was specified but not found - this is a config error
      console.warn(`[LootBehavior] Image point "${imagePointName}" not found on ${this.entity.texture?.key}, using entity center`);
    }
    // Fallback to entity center
    return { x: this.entity.x, y: this.entity.y };
  }

  /**
   * Get configured drops (for external inspection)
   */
  getDrops(): LootDrop[] {
    return this.drops;
  }

  /**
   * Update drops configuration at runtime
   */
  setDrops(drops: LootDrop[]): void {
    this.drops = drops;
  }

  /**
   * Reset the trigger state (allows loot to drop again)
   */
  reset(): void {
    this.hasTriggered = false;
  }

  override destroy(): void {
    // Event listeners are cleaned up when entity is destroyed
    super.destroy();
  }
}
